import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

export interface ListColumn<T> {
  /** Stable React key for the column. */
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  /** Extra classes for the cell (font, colour, width). Alignment comes from `align`. */
  className?: string;
  render: (row: T) => ReactNode;
}

interface ListTableProps<T> {
  rows: readonly T[];
  columns: readonly ListColumn<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  /** Shown when there are no rows: an icon, a sentence (or the load error), and optionally an action. */
  empty: { icon: LucideIcon; message: string; action?: ReactNode };
}

const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

/**
 * The record table of a list page (ADR-004). Every list drew its own <table>, header styling,
 * spinner and empty state, each slightly different; the columns are the only part that really
 * differs between them, so that is all a page now provides.
 */
export function ListTable<T>({ rows, columns, rowKey, onRowClick, loading, empty }: ListTableProps<T>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (rows.length === 0) {
    const Icon = empty.icon;
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-gray-400">
        <Icon size={40} className="mb-3 opacity-40" />
        <p className="text-sm font-medium">{empty.message}</p>
        {empty.action && <div className="mt-3">{empty.action}</div>}
      </div>
    );
  }

  return (
    <table className="w-full text-sm">
      <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
        <tr>
          {columns.map(c => (
            <th
              key={c.key}
              className={cn(
                'px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap',
                alignClass[c.align ?? 'left'],
              )}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map(row => (
          <tr
            key={rowKey(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              'transition-colors group',
              onRowClick && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50',
            )}
          >
            {columns.map(c => (
              <td key={c.key} className={cn('px-4 py-3', alignClass[c.align ?? 'left'], c.className)}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
